import { TbPlus, TbX } from "solid-icons/tb";
import { type Component, For, type JSX, Show } from "solid-js";
import { onButtonKeyDown } from "../buttons/buttonKeyDown";
import { TagChip } from "./TagChip";
import type { LiveSummaryItemData, LiveSummaryTag } from "./types";

// Teal, distinct from the to-do amber and decision violet, so a note reads as
// the user's own rather than the model's at a glance.
const NOTE_BADGE = "bg-[#14b8a6]/20 text-[#0f766e] dark:bg-[#14b8a6]/25 dark:text-[#5eead4]";

/**
 * A note the user wrote, rendered as a tile instead of a bullet so it is
 * visibly theirs. Every action is optional: with none supplied this is the web
 * app's read-only card, since creating, tagging and editing all live in the
 * desktop app.
 */
export const NoteRow: Component<{
  item: LiveSummaryItemData;
  tags: readonly LiveSummaryTag[];
  onFocusTag?: (tagId: string) => void;
  onOpen?: (item: LiveSummaryItemData) => void;
  // The note's chat, rendered inside the card. A bullet row tints itself when it
  // expands, so its chat reads as part of it; the note's own border would cut a
  // chat placed after the card adrift from what it belongs to.
  expandedContent?: JSX.Element;
  // Set aside on an active note, restore on a set-aside one. Callers pass
  // whichever matches the section the card is rendered in.
  onDrop?: (id: string) => void;
  onRestore?: (id: string) => void;
}> = (props) => {
  const itemTags = () => props.tags.filter((t) => (props.item.tagIds ?? []).includes(t.id));

  return (
    <div
      role="button"
      tabIndex={props.onOpen ? 0 : -1}
      aria-disabled={!props.onOpen}
      class="group/note flex flex-col gap-1.5 rounded-md border border-note/30 bg-tile px-2.5 py-2"
      classList={{ "cursor-pointer hover:border-note/60": Boolean(props.onOpen) }}
      onClick={() => props.onOpen?.(props.item)}
      onKeyDown={onButtonKeyDown(() => props.onOpen?.(props.item))}
    >
      <div class="flex flex-wrap items-center gap-1.5">
        <span
          class={`rounded px-1 py-0.5 text-[calc(0.5rem*var(--font-scale,1))] leading-none font-semibold uppercase ${NOTE_BADGE}`}
        >
          Note
        </span>
        <For each={itemTags()}>
          {(tag) => <TagChip tag={tag} onClick={props.onFocusTag && (() => props.onFocusTag?.(tag.id))} />}
        </For>
        {/* Controls sit top-right, away from the badge and the tags already on
            the note. The card opens the transcript, so they must not. */}
        <div
          role="presentation"
          class="ml-auto flex shrink-0 items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <Show when={props.onDrop}>
            <button
              type="button"
              title="Set aside"
              onClick={() => props.onDrop?.(props.item.id)}
              class="grid size-4.5 shrink-0 cursor-pointer place-items-center rounded-md text-muted-text opacity-0 transition-opacity group-hover/note:opacity-100 hover:bg-danger/10 hover:text-danger"
            >
              <TbX size={12} />
            </button>
          </Show>
          <Show when={props.onRestore}>
            <button
              type="button"
              title="Restore to summary"
              onClick={() => props.onRestore?.(props.item.id)}
              class="grid size-4.5 shrink-0 cursor-pointer place-items-center rounded-md text-muted-text opacity-0 transition-opacity group-hover/note:opacity-100 hover:bg-brand/10 hover:text-brand"
            >
              <TbPlus size={12} />
            </button>
          </Show>
        </div>
      </div>

      <p class="text-[calc(0.85rem*var(--font-scale,1))] whitespace-pre-wrap text-primary-text">{props.item.text}</p>

      <Show when={props.item.quote}>
        {(quote) => <p class="border-l-2 border-note/50 pl-2 text-xs text-muted-text italic">“{quote()}”</p>}
      </Show>

      <div class="flex flex-wrap items-center gap-2.5 text-[calc(0.625rem*var(--font-scale,1))] text-muted-text">
        <Show when={props.item.owner}>{(owner) => <span>{owner()}</span>}</Show>
      </div>

      <Show when={props.expandedContent}>
        {(content) => (
          // Bleeds to the card's edges so the divider spans it, and swallows its
          // own clicks — the card toggles the chat, so a click in it would close
          // what the user is typing into.
          <div
            role="presentation"
            class="-mx-2.5 -mb-2 cursor-default border-t border-note/25"
            onClick={(e) => e.stopPropagation()}
          >
            {content()}
          </div>
        )}
      </Show>
    </div>
  );
};
